<script setup lang="ts">
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { filterPickerOptions } from '@/domain/store-setup/location-step';
  import type { IPickerOption } from '@/domain/store-setup/location-step';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * A search box over a plain `role="listbox"` (plan §4.1, §4.2): the panel of
   * the country chip and the currency «Cambiar» popovers. Arrow keys move,
   * Enter picks, rows are ≥ 44 px. It lives inside a `PosUiPopover` panel, so
   * it is mounted when the popover opens.
   */
  const props = withDefaults(
    defineProps<{
      options: IPickerOption[];
      value?: string | null;
      /** Placeholder and accessible name of the search box. */
      searchLabel: string;
      /** Accessible name of the list. */
      listLabel: string;
      /** Shown when the search matches nothing. */
      emptyText: string;
      /** Shows the search box (short lists need none: arrows move on the list). */
      searchable?: boolean;
    }>(),
    { value: null, searchable: true }
  );
  const emit = defineEmits<{ (e: 'select', value: string): void }>();

  const uid = nextDomId('setup-picker');
  const listId = `${uid}-list`;
  const root = ref<HTMLElement | null>(null);
  const listRef = ref<HTMLElement | null>(null);
  const query = ref('');
  const activeIndex = ref(-1);
  let focusTimer: ReturnType<typeof setTimeout> | null = null;

  const filtered = computed(() =>
    filterPickerOptions(props.options, query.value)
  );
  const activeId = computed(() =>
    activeIndex.value >= 0 && activeIndex.value < filtered.value.length
      ? optionId(activeIndex.value)
      : undefined
  );

  function optionId(index: number): string {
    return `${uid}-option-${index}`;
  }

  function onQuery(value: string) {
    query.value = value;
    activeIndex.value = filtered.value.length ? 0 : -1;
  }

  /**
   * Scrolls only the list (never `scrollIntoView`: the list sits in a fixed
   * popover panel, and the page around it must not move).
   */
  async function revealActive() {
    await nextTick();
    const id = activeId.value;
    const list = listRef.value;
    if (!id || !list) return;
    const row = list.querySelector(`#${id}`);
    if (!(row instanceof HTMLElement)) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight)
      list.scrollTop = bottom - list.clientHeight;
  }

  function move(step: number) {
    const count = filtered.value.length;
    if (!count) return;
    const start =
      activeIndex.value < 0 ? (step > 0 ? -1 : count) : activeIndex.value;
    activeIndex.value = Math.min(count - 1, Math.max(0, start + step));
    void revealActive();
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Enter') {
      const option = filtered.value[activeIndex.value];
      if (!option) return;
      event.preventDefault();
      emit('select', option.value);
    }
  }

  // The list changes under the cursor when the options change (e.g. the locale).
  watch(filtered, (list) => {
    if (activeIndex.value >= list.length) activeIndex.value = list.length - 1;
  });

  onMounted(() => {
    activeIndex.value = Math.max(
      -1,
      filtered.value.findIndex((option) => option.value === props.value)
    );
    void revealActive();
    // A mouse user types right away; on touch screens the keyboard stays closed until asked for.
    const finePointer =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!finePointer) return;
    // The popover panel stays invisible (unfocusable) until it is positioned.
    focusTimer = setTimeout(() => {
      focusTimer = null;
      const target = props.searchable
        ? root.value?.querySelector('input')
        : listRef.value;
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }, 0);
  });

  onBeforeUnmount(() => {
    if (focusTimer !== null) clearTimeout(focusTimer);
  });
</script>

<template>
  <div ref="root" class="v2-setup-picker-list">
    <PosUiInput
      v-if="searchable"
      :value="query"
      icon="magnify"
      size="sm"
      autocomplete="off"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="true"
      :aria-controls="listId"
      :aria-activedescendant="activeId"
      :aria-label="searchLabel"
      :placeholder="searchLabel"
      @input="onQuery"
      @keydown="onKeydown"
    />
    <ul
      :id="listId"
      ref="listRef"
      class="picker-options"
      role="listbox"
      :aria-label="listLabel"
      :tabindex="searchable ? undefined : 0"
      :aria-activedescendant="searchable ? undefined : activeId"
      @keydown="searchable ? undefined : onKeydown($event)"
    >
      <li
        v-for="(option, index) in filtered"
        :id="optionId(index)"
        :key="option.value"
        class="picker-option"
        :class="{
          'is-active': index === activeIndex,
          'is-selected': option.value === value,
        }"
        role="option"
        :aria-selected="String(option.value === value)"
        @mousedown.prevent
        @mousemove="activeIndex = index"
        @click="emit('select', option.value)"
      >
        <!-- #option: extra content before the label (a status dot). -->
        <slot name="option" :option="option" />
        <span class="option-label">{{ option.label }}</span>
        <PosUiIcon
          v-if="option.value === value"
          name="check"
          class="option-check"
        />
      </li>
    </ul>
    <p v-if="!filtered.length" class="picker-empty">{{ emptyText }}</p>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-picker-list {
    // Margins, not flex gap: old POS hardware (Chrome < 84).
    @apply tw-flex tw-flex-col;
    // Relative: the rows' offsetTop is measured from the list (see revealActive).
    .picker-options {
      @apply tw-relative tw-m-0 tw-flex tw-list-none tw-flex-col tw-overflow-y-auto tw-p-0;
      // = min(40vh, 15rem), which old Chrome lacks: 40vh below 600px.
      max-height: 15rem;

      @media (max-height: 600px) {
        max-height: 40vh;
      }
    }
    > * + .picker-options {
      @apply tw-mt-2;
    }
    .picker-option {
      @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-rounded-lg tw-px-3 tw-text-sm tw-text-neutral-700;
      &.is-active {
        @apply tw-bg-neutral-100;
      }
      &.is-selected {
        @apply tw-font-semibold tw-text-neutral-900;
      }
    }
    .option-label {
      @apply tw-min-w-0 tw-flex-1 tw-truncate;
    }
    .option-check {
      @apply tw-ml-2 tw-shrink-0 tw-text-lg tw-text-neutral-900;
    }
    .picker-empty {
      @apply tw-m-0 tw-mt-2 tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-500;
    }
  }
</style>
